/* section-kit-selector.js - Pattern Kit Selector */

class PatternKitSelector {
  constructor() {
    this.root     = document.getElementById('kit-selector');
    this.modal    = document.getElementById('kit-modal');
    if (!this.root || !this.modal) return;

    this.req       = JSON.parse(this.root.dataset.yarnRequirements);
    this.name      = this.root.dataset.patternName || '';
    this.cards     = Array.from(this.root.querySelectorAll('[data-kit-card]'));
    this.sizeEl    = this.root.querySelector('[data-kit-size]');
    this.sizeHint  = this.root.querySelector('[data-kit-size-hint]');
    this.addBtn    = document.getElementById('kit-add-to-cart');
    this.totalEl   = this.root.querySelector('[data-kit-total]');
    this.search    = this.modal.querySelector('[data-modal-search]');
    this.colorGrid = this.modal.querySelector('[data-color-grid]');
    this.stockNote = this.modal.querySelector('[data-stock-note]');

    this.choice          = this.req.yarn_type_choice && this.req.yarn_type_choice.options ? this.req.yarn_type_choice : null;
    this.yarnTypeTabs    = Array.from(this.root.querySelectorAll('[data-yarn-type-tab]'));
    this.yarnTypeRow     = this.root.querySelector('[data-yarn-type-tabs]');
    this.yarnTypeHint    = this.root.querySelector('[data-yarn-type-hint]');
    this.selectedYarnHandle = null;
    this.selectedYarnLabel  = null;

    this.selections    = {};   // { cardIndex: { variantId, price, qty } }
    this.activeCard    = null;

    this._init();
  }

  _init() {
    // Move modal to <body> so fixed positioning works correctly
    if (this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
    }

    this._bindYarnTypeChoice();
    this._bindCards();
    this._bindSize();
    this._bindModal();
    this._bindAccessories();
    this._bindAddToCart();
    this._updateTotal();
  }

  // ── Yarn-type choice (cross-slot) ──────────────────────────────────

  _bindYarnTypeChoice() {
    if (!this.choice) return;

    // Validate config: every option handle must resolve to at least one swatch.
    // One-sided check — extra products in compatible_yarn_products are fine.
    const allSwatches = Array.from(this.colorGrid.querySelectorAll('[data-variant-id]'));
    this.choice.options.forEach(opt => {
      const hasAny = allSwatches.some(s => s.dataset.productHandle === opt.handle);
      if (!hasAny) {
        console.warn(`[kit-selector] yarn_type_choice option "${opt.handle}" has no matching product in compatible_yarn_products.`);
      }
    });

    // Validate scope: every label in applies_to_labels must match a slot.
    // One-sided — slots that aren't listed are intentionally out of scope.
    const appliesTo = this.choice.applies_to_labels;
    if (Array.isArray(appliesTo) && appliesTo.length > 0) {
      const slotLabels = this.cards.map(c => c.dataset.yarnLabel);
      appliesTo.forEach(label => {
        if (!slotLabels.includes(label)) {
          console.warn(`[kit-selector] yarn_type_choice.applies_to_labels contains "${label}" but no slot with that label exists.`);
        }
      });
    }

    // Sold-out detection: a tab is disabled if every swatch for its handle has inventory <= 0.
    this.yarnTypeTabs.forEach(tab => {
      const handle = tab.dataset.yarnTypeHandle;
      const swatches = allSwatches.filter(s => s.dataset.productHandle === handle);
      const anyInStock = swatches.some(s => (parseInt(s.dataset.inventory) || 0) > 0);
      if (swatches.length > 0 && !anyInStock) {
        tab.disabled = true;
        tab.setAttribute('aria-disabled', 'true');
        const badge = tab.querySelector('[data-soldout-badge]');
        if (badge) badge.hidden = false;
      }
    });

    this.yarnTypeTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        if (tab.disabled) return;
        this._selectYarnType(tab);
      });
    });
  }

  _selectYarnType(tab) {
    const handle = tab.dataset.yarnTypeHandle;
    const label  = tab.dataset.yarnTypeLabel;
    if (this.selectedYarnHandle === handle) return;

    this.selectedYarnHandle = handle;
    this.selectedYarnLabel  = label;

    this.yarnTypeTabs.forEach(t => {
      const active = t === tab;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-checked', active ? 'true' : 'false');
    });

    this.root.classList.remove('kit-selector--yarn-type-pending');

    // Clear hint
    if (this.yarnTypeHint) {
      this.yarnTypeHint.textContent = '';
      this.yarnTypeHint.classList.remove('is-visible');
    }

    // Yarn-type change invalidates every variant — same reset as size change.
    this.selections = {};
    this.cards.forEach(card => this._resetCard(card));
    this._updateTotal();
  }

  _yarnTypeGate(card) {
    if (!this.choice || this.selectedYarnHandle) return true;
    // Card not scoped to the choice — let it through (e.g. fixed-yarn slot
    // alongside a choice-scoped slot, as in held-strands kits like Carlie Tee).
    if (card && !card.hasAttribute('data-strict-filter')) return true;
    if (this.yarnTypeRow) this._shakeEl(this.yarnTypeRow);
    if (this.yarnTypeHint) {
      this.yarnTypeHint.textContent = bfT('choose_yarn_type_first', 'Wähle zuerst einen Garn-Typ.');
      this.yarnTypeHint.classList.add('is-visible');
      setTimeout(() => this.yarnTypeHint.classList.remove('is-visible'), 3000);
    }
    return false;
  }

  // ── Cards ──────────────────────────────────────────────────────────

  _bindCards() {
    this.cards.forEach((card, i) => {
      card.addEventListener('click', () => this._openModal(i));
    });
  }

  // ── Size selector ──────────────────────────────────────────────────

  _bindSize() {
    if (!this.sizeEl) return;
    this.sizeEl.addEventListener('change', () => {
      const sizeData = this.req.sizes.find(s => s.name === this.sizeEl.value);
      if (!sizeData) return;

      this._applySizeQuantities(sizeData);

      // Reset colour selections (quantities changed, old selections may be invalid)
      this.selections = {};
      this.cards.forEach(card => this._resetCard(card));
      this._updateTotal();
    });
  }

  // Writes the per-slot quantities of a size onto the cards. Slots the size
  // doesn't need (qty 0, e.g. "S · Merino pur") are hidden so they neither
  // confuse nor block the add-to-cart button. No sizeData → back to the
  // initial "- Knäuel" state with every slot visible.
  _applySizeQuantities(sizeData) {
    this.cards.forEach(card => {
      const qty = sizeData ? (sizeData.quantities[card.dataset.yarnLabel] || 0) : null;
      card.querySelector('[data-card-quantity]').value = qty === null ? '' : qty;
      card.querySelector('[data-card-qty]').textContent = `${qty === null ? '-' : qty} ${bfT('balls', 'Knäuel')}`;
      const wrap = card.closest('.kit-yarn-card-wrap');
      if (wrap) wrap.hidden = qty === 0;
    });
  }

  // A slot is needed unless the chosen size assigns it zero skeins.
  _isCardNeeded(card) {
    if (this.req.size_based && this.sizeEl && !this.sizeEl.value) return true;
    return (parseInt(card.querySelector('[data-card-quantity]').value) || 0) > 0;
  }

  // ── Modal ──────────────────────────────────────────────────────────

  _bindModal() {
    // Close triggers
    this.modal.querySelectorAll('[data-modal-close]').forEach(el => {
      el.addEventListener('click', () => this._closeModal());
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !this.modal.hasAttribute('inert')) this._closeModal();
    });

    // Colour swatches
    this.colorGrid.querySelectorAll('[data-variant-id]').forEach(swatch => {
      swatch.addEventListener('click', () => this._selectColour(swatch));
    });

    // Search
    this.search.addEventListener('input', () => this._filterColours(this.search.value));
  }

  _openModal(cardIndex) {
    const card = this.cards[cardIndex];

    // Guard: require yarn-type selection first (only for scoped slots)
    if (!this._yarnTypeGate(card)) return;

    // Guard: require size selection first
    if (this.req.size_based && this.sizeEl && !this.sizeEl.value) {
      this._shakeEl(this.sizeEl);
      if (this.sizeHint) {
        this.sizeHint.textContent = bfT('choose_size_first', 'Bitte wähle zuerst eine Größe.');
        this.sizeHint.classList.add('is-visible');
        setTimeout(() => this.sizeHint.classList.remove('is-visible'), 3000);
      }
      this.sizeEl.focus();
      return;
    }

    this.activeCard = cardIndex;
    const yarnType = card.dataset.yarnType;
    const label    = card.dataset.yarnLabel;
    const reqQty   = parseInt(card.querySelector('[data-card-quantity]').value) || 0;

    // Update modal title
    this.modal.querySelector('[data-modal-title]').textContent = `${label} - Farbe wählen`;

    // Strict-by-handle filter applies only when (a) a yarn-type is selected
    // AND (b) this card is scoped to the choice (has data-strict-filter).
    // Otherwise fall back to the original fuzzy yarn-type match — that path
    // handles both no-choice kits and the non-scoped slots of mixed kits
    // (e.g. Carlie Tee's fixed Mohair slot).
    const allSwatches = Array.from(this.colorGrid.querySelectorAll('[data-variant-id]'));
    let swatchMatches;
    let useTypeFilter = true;
    const useStrict = this.selectedYarnHandle && card.hasAttribute('data-strict-filter');

    if (useStrict) {
      const chosen = this.selectedYarnHandle;
      swatchMatches = swatch => swatch.dataset.productHandle === chosen;
      // No fuzzy fallback here — empty modal is the correct loud signal
      // when handles are misconfigured (the console.warn at init already fired).
    } else {
      // Original fuzzy match: card yarn_type substring-matches swatch yarn_type
      // after lowercase + alphanumeric normalization (e.g. "Soft Silk Mohair"
      // matches "Knitting for Olive Soft Silk Mohair").
      const norm = s => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
      const cardKey = norm(yarnType);
      swatchMatches = swatch => {
        const sk = norm(swatch.dataset.yarnType);
        if (!cardKey || !sk) return false;
        return sk.includes(cardKey) || cardKey.includes(sk);
      };
      // If nothing matches at all (truly unrelated catalog), fall back to
      // showing every compatible swatch so the modal isn't a dead end.
      const matchesByType = allSwatches.filter(swatchMatches);
      useTypeFilter = matchesByType.length > 0;
    }

    // Show/hide swatches based on yarn type + stock. Stock is checked against
    // what's LEFT after the same colour is committed to other slots, so the
    // same low-stock colour can't be over-picked across multiple slots.
    // Untracked / continue-selling variants (data-track="0") are unlimited.
    let hidden = 0;
    allSwatches.forEach(swatch => {
      const matchesType = useTypeFilter ? swatchMatches(swatch) : true;
      const tracked     = swatch.dataset.track === '1';
      const stock       = parseInt(swatch.dataset.inventory);
      const committed   = this._committedQtyForVariant(swatch.dataset.variantId, cardIndex);
      const remaining   = (isNaN(stock) ? Infinity : stock) - committed;
      const hasStock    = !tracked || remaining >= reqQty;
      const show        = matchesType && hasStock;

      swatch.hidden = !show;
      if (matchesType && !hasStock) hidden++;

      // Restore selection state
      const sel = this.selections[cardIndex];
      swatch.classList.toggle('is-selected', !!(sel && sel.variantId === swatch.dataset.variantId));

      // Show remaining-stock count (after other slots' claims) on the swatch
      const stockEl = swatch.querySelector('[data-swatch-stock]');
      if (stockEl && tracked && remaining < 10 && remaining > 0) {
        stockEl.textContent = bfT('n_available', 'noch {count} verfügbar').replace('{count}', remaining);
      } else if (stockEl) {
        stockEl.textContent = '';
      }
    });

    // Stock notice (silent fallback to all-swatches if metafield unset).
    if (hidden > 0 && reqQty > 0) {
      this.stockNote.textContent = (hidden === 1 ? bfT('colors_hidden_one', '{count} Farbe ausgeblendet – nicht genug Lagerbestand für {qty} Knäuel.') : bfT('colors_hidden_other', '{count} Farben ausgeblendet – nicht genug Lagerbestand für {qty} Knäuel.')).replace('{count}', hidden).replace('{qty}', reqQty);
      this.stockNote.hidden = false;
    } else {
      this.stockNote.hidden = true;
    }

    // Clear search
    this.search.value = '';
    this._filterColours('');

    // Open
    this.modal.removeAttribute('inert');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.search.focus();
  }

  _closeModal() {
    this.modal.setAttribute('inert', '');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.activeCard = null;
    this.search.value = '';
    this._filterColours('');
    this.stockNote.hidden = true;
    this.colorGrid.querySelectorAll('[data-variant-id]').forEach(s => s.classList.remove('is-selected'));
    // Return focus to active card
    if (this.activeCard !== null) this.cards[this.activeCard]?.focus();
  }

  _selectColour(swatch) {
    if (this.activeCard === null) return;
    const card  = this.cards[this.activeCard];
    const qty   = parseInt(card.querySelector('[data-card-quantity]').value) || 0;
    const price = parseInt(swatch.dataset.variantPrice);
    const id    = swatch.dataset.variantId;
    const title = swatch.dataset.variantTitle;

    this.selections[this.activeCard] = { variantId: id, price, qty };

    // Update card UI
    const img = swatch.querySelector('img');
    const preview = card.querySelector('[data-card-preview]');
    if (img) {
      preview.innerHTML = `<img src="${img.src}" alt="${title}" class="kit-card__preview-img">`;
    }
    card.querySelector('[data-card-color-name]').textContent = title;
    card.querySelector('[data-card-qty]').textContent = `${qty} Knäuel`;
    card.querySelector('[data-card-price]').textContent = this._money(price);
    card.querySelector('[data-card-variant-id]').value = id;
    card.classList.add('is-selected');

    this._updateTotal();
    this._closeModal();
  }

  // Total quantity of a variant already committed to slots other than the
  // given one — used to keep cross-slot picks within available stock.
  _committedQtyForVariant(variantId, excludeIndex) {
    let sum = 0;
    Object.keys(this.selections).forEach(k => {
      if (parseInt(k, 10) === excludeIndex) return;
      const s = this.selections[k];
      if (s && s.variantId === variantId) sum += s.qty;
    });
    return sum;
  }

  _resetCard(card) {
    card.querySelector('[data-card-preview]').innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="kit-card__placeholder icon"><circle cx="12" cy="12" r="10" stroke-width="1.5" stroke-dasharray="3 3"/></svg>`;
    card.querySelector('[data-card-color-name]').textContent = bfT('choose_color', 'Farbe wählen');
    card.querySelector('[data-card-price]').textContent = '-';
    card.querySelector('[data-card-variant-id]').value = '';
    card.classList.remove('is-selected');
  }

  _filterColours(term) {
    const t = term.toLowerCase().trim();
    this.colorGrid.querySelectorAll('[data-variant-id]').forEach(swatch => {
      if (swatch.hidden) return; // already filtered by yarn type
      swatch.classList.toggle('is-hidden', !!(t && !swatch.dataset.search.includes(t)));
    });
  }

  // ── Accessories ────────────────────────────────────────────────────

  _bindAccessories() {
    const needlesCb   = this.root.querySelector('[data-optional-type="needles"]');
    const needlesWrap = this.root.querySelector('[data-needles-wrap]');
    const needleSel   = this.root.querySelector('[data-needles-select]');
    const needlesPrice = this.root.querySelector('.kit-needles-price');
    const needlesVariant = needlesCb?.closest('.kit-accessory')?.querySelector('.kit-accessory__variant');

    if (needlesCb && needlesWrap) {
      needlesCb.addEventListener('change', () => {
        needlesWrap.hidden = !needlesCb.checked;
        if (!needlesCb.checked && needleSel) {
          needleSel.value = '';
          if (needlesPrice) needlesPrice.textContent = '-';
          if (needlesVariant) needlesVariant.value = '';
        }
        this._updateTotal();
      });
    }

    if (needleSel) {
      needleSel.addEventListener('change', () => {
        const opt = needleSel.options[needleSel.selectedIndex];
        if (opt?.value) {
          if (needlesPrice) needlesPrice.textContent = this._money(parseInt(opt.dataset.price));
          if (needlesVariant) needlesVariant.value = opt.value;
        }
        this._updateTotal();
      });
    }

    // All other accessory checkboxes
    this.root.querySelectorAll('.kit-accessory__cb').forEach(cb => {
      if (cb !== needlesCb) cb.addEventListener('change', () => this._updateTotal());
    });
  }

  // ── Total & button ─────────────────────────────────────────────────

  _updateTotal() {
    let total      = 0;
    let allPicked  = true;

    // Yarn selections — only slots the chosen size actually needs
    this.cards.forEach((card, i) => {
      const sel = this.selections[i];
      if (sel) {
        total += sel.price * sel.qty;
      } else if (this._isCardNeeded(card)) {
        allPicked = false;
      }
    });

    // Size guard
    if (this.req.size_based && this.sizeEl && !this.sizeEl.value) allPicked = false;

    // Accessories
    this.root.querySelectorAll('.kit-accessory__cb:checked').forEach(cb => {
      const wrap    = cb.closest('.kit-accessory');
      const variant = wrap?.querySelector('.kit-accessory__variant')?.value;
      const qty     = parseInt(wrap?.querySelector('.kit-accessory__qty')?.value) || 1;
      if (!variant) return;

      if (cb.dataset.optionalType === 'needles') {
        const needleSel = this.root.querySelector('[data-needles-select]');
        const opt = needleSel?.options[needleSel.selectedIndex];
        if (opt?.value) total += parseInt(opt.dataset.price) * qty;
      } else {
        // Price already formatted in the page - read from the swatch or use 0
        // We rely on the hidden variant to get price via the cart API; skip here.
        // If stitch markers price is needed, fetch from Shopify storefront.
      }
    });

    if (this.totalEl) this.totalEl.textContent = this._money(total);
    if (this.addBtn)  this.addBtn.disabled = !allPicked;
  }

  // ── Add to cart ────────────────────────────────────────────────────

  _bindAddToCart() {
    if (!this.addBtn) return;
    this.addBtn.addEventListener('click', () => this._addToCart());
  }

  async _addToCart() {
    if (this.addBtn.disabled) return;

    const labelEl = this.addBtn.querySelector('.kit-cta__label');
    const origLabel = labelEl.textContent;

    this.addBtn.disabled = true;
    labelEl.textContent = bfT('adding', 'Wird hinzugefügt …');

    try {
      const items = [];
      const size  = this.sizeEl?.value || null;

      // Yarn items
      this.cards.forEach((card, i) => {
        const sel = this.selections[i];
        if (!sel || sel.qty <= 0) return;
        const props = { 'Anleitung': this.name, 'Verwendung': card.dataset.yarnLabel };
        if (size) props['Größe'] = size;
        // Garn-Typ stays on scoped slots only — a fixed-yarn slot in a mixed
        // kit (e.g. Carlie Tee mohair) shouldn't carry the choice label.
        if (this.selectedYarnLabel && card.hasAttribute('data-strict-filter')) {
          props['Garn-Typ'] = this.selectedYarnLabel;
        }
        items.push({ id: parseInt(sel.variantId), quantity: sel.qty, properties: props });
      });

      // Accessories
      this.root.querySelectorAll('.kit-accessory__cb:checked').forEach(cb => {
        const wrap    = cb.closest('.kit-accessory');
        const variantId = wrap?.querySelector('.kit-accessory__variant')?.value;
        const qty     = parseInt(wrap?.querySelector('.kit-accessory__qty')?.value) || 1;
        if (!variantId) return;

        // For needles, use the selected variant, not the hidden one
        if (cb.dataset.optionalType === 'needles') {
          const needleSel = this.root.querySelector('[data-needles-select]');
          if (needleSel?.value) {
            const opt = needleSel.options[needleSel.selectedIndex];
            const props = { 'Anleitung': this.name, 'Variante': opt.dataset.title };
            if (size) props['Größe'] = size;
            items.push({ id: parseInt(needleSel.value), quantity: qty, properties: props });
          }
        } else {
          const props = { 'Anleitung': this.name };
          if (size) props['Größe'] = size;
          items.push({ id: parseInt(variantId), quantity: qty, properties: props });
        }
      });

      const res  = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      const data = await res.json();

      if (!res.ok) {
        if (res.status === 422 && data.description) {
          labelEl.textContent = bfT('partial_added', '⚠ Teilweise hinzugefügt');
          this.addBtn.classList.add('is-partial');
          setTimeout(() => { this._resetForm(); labelEl.textContent = origLabel; this.addBtn.classList.remove('is-partial'); }, 2500);
          this._refreshCartCount();
          return;
        }
        throw new Error(data.description || 'Cart error');
      }

      labelEl.textContent = bfT('added', '✓ Hinzugefügt') + '!';
      this.addBtn.classList.add('is-success');
      this._refreshCartCount();

      setTimeout(() => {
        this._resetForm();
        labelEl.textContent = origLabel;
        this.addBtn.classList.remove('is-success');
      }, 1800);

    } catch (err) {
      console.error('[kit-selector] addToCart:', err);
      labelEl.textContent = bfT('error_retry', 'Fehler – erneut versuchen');
      this.addBtn.classList.add('is-error');
      setTimeout(() => {
        labelEl.textContent = origLabel;
        this.addBtn.classList.remove('is-error');
        this.addBtn.disabled = false;
      }, 3000);
    }
  }

  _refreshCartCount() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        document.dispatchEvent(new CustomEvent('boni:cart:added', { detail: cart }));
      })
      .catch(() => {});
  }

  _resetForm() {
    if (this.sizeEl) {
      this.sizeEl.value = '';
      this._applySizeQuantities(null);
    }
    this.selections = {};
    this.cards.forEach(card => this._resetCard(card));
    this.root.querySelectorAll('.kit-accessory__cb').forEach(cb => { cb.checked = false; });
    const needlesWrap = this.root.querySelector('[data-needles-wrap]');
    if (needlesWrap) needlesWrap.hidden = true;
    const needleSel = this.root.querySelector('[data-needles-select]');
    if (needleSel) needleSel.value = '';
    const needlesPrice = this.root.querySelector('.kit-needles-price');
    if (needlesPrice) needlesPrice.textContent = '-';

    // Reset yarn-type selection if configured
    if (this.choice) {
      this.selectedYarnHandle = null;
      this.selectedYarnLabel  = null;
      this.yarnTypeTabs.forEach(t => {
        t.classList.remove('is-active');
        t.setAttribute('aria-checked', 'false');
      });
      this.root.classList.add('kit-selector--yarn-type-pending');
    }

    this._updateTotal();
  }

  // ── Helpers ────────────────────────────────────────────────────────

  _money(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' EUR';
  }

  _shakeEl(el) {
    el.classList.remove('shake');
    void el.offsetWidth; // reflow
    el.classList.add('shake');
    el.addEventListener('animationend', () => el.classList.remove('shake'), { once: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PatternKitSelector());
} else {
  new PatternKitSelector();
}
